'use client'

import { useState, useCallback, useEffect, useRef, TouchEvent } from 'react'
import { storyblokEditable } from '@storyblok/react/rsc'
import Image from 'next/image'
import { SlideStoryblok, SliderStoryblok } from '@/component-types-sb'

export default function Slider({ blok }: { blok: SliderStoryblok }) {
  // Slider images are displayed at a fixed 480px height — crop to that
  const sliderImageLoader = useCallback(
    ({ src, width, quality }: { src: string; width: number; quality?: number }) => {
      if (!src.includes('a.storyblok.com') || src.endsWith('.svg')) return src
      return `${src}/m/${width}x480/smart/filters:format(webp):quality(${quality ?? 65})`
    },
    []
  )

  const {
    slides = [],
    title = '',
    auto_play = 0,
    pause_on_hover = true,
    show_arrows = true,
    show_dots = true,
    overlay = true,
  } = blok

  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const [autoPlayKey, setAutoPlayKey] = useState(0) // Used to reset auto-play timer
  const totalSlides = slides.length

  // Touch/swipe state
  const touchStartX = useRef<number | null>(null)
  const touchEndX = useRef<number | null>(null)
  const minSwipeDistance = 50

  // Reset auto-play timer
  const resetAutoPlay = useCallback(() => {
    setAutoPlayKey((prev) => prev + 1)
  }, [])

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index)
    resetAutoPlay()
  }, [resetAutoPlay])

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? totalSlides - 1 : prev - 1))
    resetAutoPlay()
  }, [totalSlides, resetAutoPlay])

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === totalSlides - 1 ? 0 : prev + 1))
  }, [totalSlides])

  // Wrapper for manual next (resets timer)
  const goToNextManual = useCallback(() => {
    goToNext()
    resetAutoPlay()
  }, [goToNext, resetAutoPlay])

  // Auto-play functionality
  const autoPlayMs = auto_play ? Number(auto_play) * 1000 : 0
  useEffect(() => {
    if (autoPlayMs <= 0 || isPaused || totalSlides <= 1) return

    const interval = setInterval(() => {
      goToNext()
    }, autoPlayMs)

    return () => clearInterval(interval)
  }, [autoPlayMs, isPaused, goToNext, totalSlides, autoPlayKey])

  // Touch handlers for swipe gestures
  const onTouchStart = (e: TouchEvent) => {
    touchEndX.current = null
    touchStartX.current = e.targetTouches[0].clientX
  }

  const onTouchMove = (e: TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX
  }

  const onTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return

    const distance = touchStartX.current - touchEndX.current
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe) {
      goToNextManual()
    } else if (isRightSwipe) {
      goToPrevious()
    }

    touchStartX.current = null
    touchEndX.current = null
  }

  if (totalSlides === 0) return null

  // Render responsive image using Next.js Image component
  const renderSlideImage = (slide: SlideStoryblok, index: number) => {
    const mainImage = slide.image?.filename
    const alt = slide.image?.alt || slide.title || ''

    if (!mainImage) return null

    return (
      <div className="relative w-full h-[480px]">
        <Image
          src={mainImage}
          loader={sliderImageLoader}
          alt={alt}
          fill
          sizes="(max-width: 854px) 100vw, (max-width: 1100px) 100vw, 1200px"
          className="object-cover"
          priority={index === 0}
        />
      </div>
    )
  }

  return (
    <section
      {...storyblokEditable(blok)}
      className="overflow-x-clip"
      onMouseEnter={() => pause_on_hover && setIsPaused(true)}
      onMouseLeave={() => pause_on_hover && setIsPaused(false)}
    >
      {/* Title - outside the slider container */}
      {title && 
        <div className="max-w-[1200px] min-h-[20px] mx-auto relative">
          <h2 className="absolute border-4 border-black bg-white py-2 px-2 left-[10px] md:left-[36px] -bottom-5 mb-0 z-1">{title}</h2>
        </div>
      }

      {/* Slider container with images, arrows, and dots */}
      <div className="relative w-full">
        {/* Slides Container - no overflow hidden to show adjacent slides */}
        <div
          className="overflow-visible"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
       <div
          className="flex transition-transform duration-300 ease-in-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {slides.map((slide, index) => {
            const slideContent = (
              <div
                key={slide._uid}
                className="relative w-full flex-shrink-0"
                {...storyblokEditable(slide)}
              >
                {/* Responsive image */}
                {renderSlideImage(slide, index)}

                {/* Optional overlay with title/description */}
                {overlay && (
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex flex-col justify-end p-6 md:p-10">
                    {slide.title && (
                      <h3 className="text-white text-2xl md:text-4xl font-bold mb-2">
                        {slide.title}
                      </h3>
                    )}
                    {slide.description && (
                      <p className="text-white/90 text-base md:text-lg max-w-2xl">
                        {slide.description}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )

            // Wrap in link if provided
            if (slide.link?.cached_url || slide.link?.url) {
              const href =
                slide.link.linktype === 'story'
                  ? `/${slide.link.cached_url}`
                  : slide.link.url || slide.link.cached_url || '#'

              return (
                <a key={slide._uid} href={href} className="block w-full flex-shrink-0">
                  {slideContent}
                </a>
              )
            }

            return slideContent
          })}
        </div>
      </div>

      {/* Left Arrow */}
      {show_arrows && totalSlides > 1 && (
        <button
          onClick={goToPrevious}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background text-foreground p-2 rounded-full shadow-md transition-all hover:scale-110"
          aria-label="Previous slide"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* Right Arrow */}
      {show_arrows && totalSlides > 1 && (
        <button
          onClick={goToNextManual}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background text-foreground p-2 rounded-full shadow-md transition-all hover:scale-110"
          aria-label="Next slide"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Dot Navigation */}
      {show_dots && totalSlides > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {slides.map((_, index: number) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentIndex
                  ? 'bg-white scale-110'
                  : 'bg-white/30 hover:bg-white/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
      </div>
    </section>
  )
}
